import { CompanyData, StockMarketData } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

interface Props {
  company: CompanyData;
  market: StockMarketData;
}

export default function ShareholderList({ company, market }: Props) {
  const { isDark } = useTheme();
  const { user } = useAuth();

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";
  const divider = isDark ? "border-white/5" : "border-gray-100";

  const shareholders = Object.values(market.shareholders)
    .sort((a, b) => b.shares - a.shares);

  const totalShares = company.totalShares;

  return (
    <div className={`rounded-2xl border ${cardBg}`}>
      <div className={`px-5 py-4 border-b ${divider} flex items-center justify-between`}>
        <h3 className={`text-sm font-bold ${textPrimary}`}>株主一覧</h3>
        <span className={`text-xs ${textSecondary}`}>{shareholders.length}名</span>
      </div>
      <div className="p-3">
        {shareholders.length === 0 ? (
          <p className={`text-sm text-center py-6 ${textSecondary}`}>株主なし</p>
        ) : (
          <div className="space-y-1">
            {shareholders.map((sh, i) => {
              const pct = (sh.shares / totalShares) * 100;
              const isMe = user?.id === sh.userId;
              const isOwner = company.ownerId === sh.userId;
              return (
                <div
                  key={sh.userId}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${rowHover} ${isMe ? (isDark ? "bg-amber-400/5" : "bg-amber-50") : ""}`}
                >
                  {/* Rank */}
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-black flex-shrink-0 ${
                    i === 0 ? "bg-amber-400/20 text-amber-400" :
                    i === 1 ? "bg-gray-400/20 text-gray-400" :
                    i === 2 ? "bg-orange-400/20 text-orange-400" :
                    isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400"
                  }`}>
                    {i + 1}
                  </div>

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold truncate ${isMe ? "text-amber-400" : textPrimary}`}>
                        {sh.userName}
                      </span>
                      {isMe && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-400 font-bold whitespace-nowrap">あなた</span>
                      )}
                      {isOwner && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-400/15 text-emerald-400 font-bold whitespace-nowrap">創業者</span>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className={`mt-1 h-1 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                      <div
                        className={`h-1 rounded-full ${isMe ? "bg-amber-400" : "bg-emerald-400"}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Shares & Pct */}
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${textPrimary}`}>{sh.shares.toLocaleString()}</p>
                    <p className={`text-xs ${textSecondary}`}>{pct.toFixed(2)}%</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Market Cap Summary */}
      <div className={`px-5 py-4 border-t ${divider} grid grid-cols-3 gap-4`}>
        <div className="text-center">
          <p className={`text-xs ${textSecondary} mb-0.5`}>時価総額</p>
          <p className={`text-sm font-black ${textPrimary}`}>
            {(company.stockPrice * totalShares / 1000000).toFixed(1)}M
          </p>
        </div>
        <div className="text-center">
          <p className={`text-xs ${textSecondary} mb-0.5`}>発行済株式</p>
          <p className={`text-sm font-black ${textPrimary}`}>
            {(totalShares / 10000).toFixed(0)}万株
          </p>
        </div>
        <div className="text-center">
          <p className={`text-xs ${textSecondary} mb-0.5`}>流通株式</p>
          <p className={`text-sm font-black ${textPrimary}`}>
            {(company.publicShares / 10000).toFixed(0)}万株
          </p>
        </div>
      </div>
    </div>
  );
}
